/**
 * 결제 방식 선택 — 8스텝의 분기점 (menu #817 M3 / #819 M5)
 *
 * ## 선택지가 없으면 단계를 건너뛴다
 *
 * 매장이 분할을 껐거나(D5) 진입자가 1명이면(D4) 나눌 이유가 없다. 그때 이 화면은
 * 아무것도 그리지 않고, 흐름은 곧바로 결제로 간다 — 판정은 `availableSplitMethods`
 * 가 하고 여기서 다시 쓰지 않는다.
 *
 * ## 금액 직접 입력의 한도는 서버가 준다
 *
 * 최소 MX$50(D10)은 PG 수수료 때문이고, 상한은 **과납이 곧 환불 작업**이기 때문이다.
 * 둘 다 서버가 준 값(`limits`)을 쓴다 — 하드코딩하면 매장이 최소액을 조정한 날
 * 화면만 옛 값으로 남는다.
 */
import { useState } from 'react';

import {
  availableSplitMethods,
  validatePartialAmount,
  type PartialAmountLimits,
  type PartialAmountVerdict,
  type SplitMethod,
} from '@/lib/splitMethods';
import type { Currency } from '@/types';
import { formatCurrency } from '@/utils';

interface Props {
  isSplitAvailable: boolean;
  comensalCount: number;
  limits: PartialAmountLimits;
  currency: Currency;
  onSelect: (method: SplitMethod) => void;
  /** 금액 직접 입력(monto)이 확정됐을 때. centavo 정수로 넘긴다. */
  onSubmitAmount: (amountMinor: number) => void;
  labels: SplitMethodLabels;
}

export interface SplitMethodLabels {
  title: string;
  methods: Record<SplitMethod, string>;
  amountPlaceholder: string;
  confirm: string;
  errors: Record<Exclude<PartialAmountVerdict, 'ok'>, string>;
}

export function SplitMethodPicker({
  isSplitAvailable,
  comensalCount,
  limits,
  currency,
  onSelect,
  onSubmitAmount,
  labels,
}: Props) {
  const [selected, setSelected] = useState<SplitMethod | null>(null);
  const [amountText, setAmountText] = useState('');
  const [verdict, setVerdict] = useState<PartialAmountVerdict | null>(null);

  const methods = availableSplitMethods({ isSplitAvailable, comensalCount });

  if (methods.length === 0) {
    return null;
  }

  const choose = (method: SplitMethod): void => {
    setSelected(method);
    setVerdict(null);
    onSelect(method);
  };

  const submitAmount = (): void => {
    // 손님은 페소로 입력하고 서버는 centavo 로 받는다. 부동소수 반올림이 1 centavo 를
    // 먹지 않도록 반올림해서 정수로 만든다.
    const amountMinor = Math.round(Number(amountText) * 100);
    const result = validatePartialAmount(amountMinor, limits);

    setVerdict(result);

    if (result === 'ok') {
      onSubmitAmount(amountMinor);
    }
  };

  return (
    <section
      className="rounded-lg border border-border bg-card p-4"
      data-testid="split-method-picker"
    >
      <h3 className="mb-3 text-sm font-semibold text-foreground">{labels.title}</h3>

      <ul className="flex flex-col gap-2">
        {methods.map((method) => (
          <li key={method}>
            <button
              type="button"
              onClick={() => { choose(method); }}
              aria-pressed={selected === method}
              className={
                selected === method
                  ? 'w-full rounded-md border border-primary bg-primary/10 px-3 py-2 text-left text-sm text-foreground'
                  : 'w-full rounded-md border border-border px-3 py-2 text-left text-sm text-foreground'
              }
              data-testid={`split-method-${method}`}
            >
              {labels.methods[method]}
            </button>
          </li>
        ))}
      </ul>

      {selected === 'monto' && (
        <div className="mt-3" data-testid="split-amount-form">
          <input
            type="number"
            inputMode="decimal"
            value={amountText}
            onChange={(event) => { setAmountText(event.target.value); }}
            placeholder={labels.amountPlaceholder}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            data-testid="split-amount-input"
          />

          <p className="mt-1 text-xs text-muted-foreground" data-testid="split-amount-limits">
            {formatCurrency(limits.minimumMinor / 100, currency)} –{' '}
            {formatCurrency(limits.outstandingMinor / 100, currency)}
          </p>

          {verdict !== null && verdict !== 'ok' && (
            <p className="mt-1 text-xs text-destructive" data-testid="split-amount-error">
              {labels.errors[verdict]}
            </p>
          )}

          <button
            type="button"
            onClick={submitAmount}
            className="mt-2 w-full rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground"
            data-testid="split-amount-confirm"
          >
            {labels.confirm}
          </button>
        </div>
      )}
    </section>
  );
}
